import { useMediaQuery } from 'react-responsive';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { useViewport } from '@/hooks';
import {
  initialPublicCategoryState,
  usePublicCategoryState,
} from '@/module/public/hooks/usePublicCategoryFilter';
import {
  initialPublicMetaState,
  publicMetaAtom,
} from '@/module/public/hooks/usePublicMetaFilter';
import { getSafeSchoolNameTKey } from '@/shared/category/getSafeSchoolNameTKey';
import {
  AllSchoolType,
  SchoolMapper,
} from '@/shared/category/util/SchoolMapper';
import { cn } from '@/utils';

import { type GnbButtonType, type GnbSubButtonType } from '../GnbButtonType';

export const useSchoolTypeDropdown = () => {
  const { t } = useTranslation(['common']);
  const [categoryState, setCategoryState] = usePublicCategoryState();
  const setMetaState = useSetAtom(publicMetaAtom);
  const { isTablet } = useViewport();
  const isTouchDevice = useMediaQuery({ query: '(pointer: coarse)' });

  // 최근 선택 표시 여부 (전체가 아닐 때만 표시)
  const shouldShowRecentSelection = Boolean(
    categoryState.schoolType && categoryState.schoolType !== 'ALL',
  );

  const createSchoolButton = (
    schoolType: string,
    extraProps?: Partial<GnbSubButtonType>,
  ): GnbSubButtonType => ({
    label: t(getSafeSchoolNameTKey(schoolType)),
    href: `/public`,
    onClick: () => {
      setCategoryState(() => ({
        ...initialPublicCategoryState,
        schoolType: schoolType.toUpperCase(),
      }));
      setMetaState(initialPublicMetaState);
    },
    className: cn('h-[44px]', extraProps?.className),
    clickEventname: 'gnb_public_click',
    clickEventProperties: {
      recent: false,
      schoolType: schoolType.toLowerCase(),
    },
    ...extraProps,
  });

  const recentSelectionButton: GnbSubButtonType = createSchoolButton(
    categoryState.schoolType,
    {
      subLabel: t('gnb.header.recentSelection'),
      className: 'border-b border-b-alternative h-[44px]',
      isHidden: !shouldShowRecentSelection,
      clickEventProperties: {
        recent: true,
        schoolType: categoryState.schoolType?.toLowerCase(),
      },
    },
  );

  const allSchoolButton: GnbSubButtonType = createSchoolButton(
    AllSchoolType.value,
    {
      clickEventProperties: { recent: false, schoolType: 'all' },
    },
  );

  const schoolTypeButtons: GnbSubButtonType[] = Array.from(
    SchoolMapper.schoolMap.values(),
  ).map(schoolType => createSchoolButton(schoolType));

  const dropdownButton: GnbButtonType = {
    label: t('header.explore.button'),
    clickEventname: 'gnb_public_click',
    interactionMode: isTablet && isTouchDevice ? 'click' : 'hover',
    subButtons: [recentSelectionButton, allSchoolButton, ...schoolTypeButtons],
  };

  return {
    dropdownButton,
  };
};
